import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, ArrowRight, ChevronDown, GraduationCap, Check, Crown, Loader2, Gift, ShieldCheck, Lock, Video, Mail, MonitorPlay } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import LessonCarousel from '../../../../components/groupchess/LessonCarousel';
import CreditActionModal from '../../../../components/groupchess/CreditActionModal';
import {
  getUsernameFromProfileId,
  getCoachByPlayerProfile,
  getPlayerProfile,
  getUpcomingLessons,
  getUpcomingIndividualSessions,
  getEnrolledLessons,
  getUserMembershipCredits,
  getUserCoachCredits,
  registerForLesson,
} from '../../../../services/firestoreService';
import { useAuth } from '../../../../contexts/AuthContext';
import { faq as defaultFaq } from '../../../../content/groupchess-content';
import useBlockStyles from '../common/useBlockStyles';
import { adjustColor, isLightColor } from '../../themeDefaults';
import { trackEvent } from '../../../../utils/analytics';
import { createMembershipCheckoutSession, claimFreeTrialLesson } from '../../../../services/membershipService';

const STYLE_OPTIONS = {
  defaultBackground: 'section',
  defaultHeadingSize: 'large',
  defaultAlignment: 'center',
  defaultButtonStyle: 'primary',
  defaultPadding: 'normal',
};

const headingScaleMap = { small: '0.8', medium: '1', large: '1.15', xlarge: '1.3' };
const paddingScaleMap = { compact: '0.6', normal: '1', spacious: '1.4' };

/** Convert hex color to "r, g, b" string for use in rgba() */
const hexToRgb = (hex) => {
  if (!hex || !hex.startsWith('#')) return '255, 255, 255';
  const num = parseInt(hex.slice(1), 16);
  return `${(num >> 16) & 255}, ${(num >> 8) & 255}, ${num & 255}`;
};

/**
 * CoachingRenderer - Renders the full coaching page experience
 * Self-contained component that fetches its own data
 * @param {boolean} isEmbedded - When true, collapses content below carousel behind "Show More"
 */
const CoachingRenderer = ({
  block, profileId, isEmbedded = false, theme = {},
  coach: coachProp, coachId: coachIdProp,
  initialPlayerProfile, initialUsername,
}) => {
  const [loading, setLoading] = useState(true);

  const { content = {} } = block;
  const styles = useBlockStyles(content, theme, STYLE_OPTIONS);
  const btnStyles = styles.getButtonStyles();

  // Compute CSS custom properties for cascading into <style> blocks
  const cssVars = {
    '--cb-section-bg': styles.sectionBackground,
    '--cb-card-bg': styles.cardBackground,
    '--cb-heading-color': styles.headingColor,
    '--cb-body-color': styles.bodyColor,
    '--cb-muted-color': styles.mutedColor,
    '--cb-accent': styles.accentColor,
    '--cb-accent-light': adjustColor(styles.accentColor, 15),
    '--cb-accent-dark': adjustColor(styles.accentColor, -15),
    '--cb-accent-rgb': hexToRgb(styles.accentColor),
    '--cb-heading-rgb': hexToRgb(styles.headingColor),
    '--cb-border-color': styles.borderColor,
    '--cb-heading-font': styles.headingFontStyle,
    '--cb-body-font': styles.bodyFontStyle,
    '--cb-heading-scale': headingScaleMap[styles.headingSize] || '1.15',
    '--cb-padding-scale': paddingScaleMap[styles.sectionPadding] || '1',
    '--cb-btn-bg': btnStyles.backgroundColor || btnStyles.background,
    '--cb-btn-color': btnStyles.color,
    '--cb-btn-border': btnStyles.border || 'none',
    '--cb-heading-align': styles.headingAlignment || 'center',
    '--cb-accent-text': isLightColor(styles.accentColor) ? '#1a1a2e' : '#ffffff',
    // Bridge --gc-* vars so shared groupchess components (LessonCarouselCard, etc.) work inside CoachingRenderer
    '--gc-text': styles.headingColor,
    '--gc-text-body': styles.bodyColor,
    '--gc-text-muted': styles.mutedColor,
    '--gc-text-light': styles.isDark ? '#64748b' : '#9ca3af',
    '--gc-primary': styles.accentColor,
    '--gc-primary-light': adjustColor(styles.accentColor, 15),
    '--gc-primary-dark': adjustColor(styles.accentColor, -15),
    '--gc-secondary': adjustColor(styles.accentColor, -15),
    '--gc-btn-text': isLightColor(styles.accentColor) ? '#1a1a2e' : '#ffffff',
    '--gc-bg': styles.sectionBackground,
    '--gc-bg-alt': styles.sectionBackground,
    '--gc-bg-card': styles.cardBackground,
    '--gc-bg-dark': styles.isDark ? '#0a0a0a' : '#f1f5f9',
    '--gc-border': styles.borderColor,
    '--gc-border-light': styles.isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
    '--gc-gradient': styles.accentColor,
    '--gc-gradient-light': `linear-gradient(135deg, rgba(${hexToRgb(styles.accentColor)}, 0.15) 0%, rgba(${hexToRgb(adjustColor(styles.accentColor, -15))}, 0.15) 100%)`,
    '--gc-success': '#10b981',
    '--gc-success-light': 'rgba(16, 185, 129, 0.2)',
    '--gc-warning': '#f59e0b',
    '--gc-warning-light': 'rgba(245, 158, 11, 0.2)',
    '--gc-error': '#ef4444',
    '--gc-shadow-sm': styles.isDark ? '0 1px 2px rgba(0, 0, 0, 0.3)' : '0 1px 2px rgba(0, 0, 0, 0.1)',
    '--gc-shadow': styles.isDark ? '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    '--gc-shadow-lg': styles.isDark ? '0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.2)' : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    '--gc-shadow-xl': styles.isDark ? '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.2)' : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '--gc-radius': '12px',
    '--gc-radius-lg': '16px',
  };

  const { currentUser } = useAuth();
  const [username, setUsername] = useState(null);
  const [coach, setCoach] = useState(null);
  const [coachId, setCoachId] = useState(null);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [isLoadingLessons, setIsLoadingLessons] = useState(true);
  const [enrolledLessonIds, setEnrolledLessonIds] = useState([]);
  const [isExpanded, setIsExpanded] = useState(!isEmbedded || !!content.startExpanded);

  // Registration flow state
  const [userCredits, setUserCredits] = useState(0);
  const [isRegistering, setIsRegistering] = useState(false);
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: 'loading',
    action: 'booking',
    lessonTitle: '',
    creditsBefore: 0,
    creditsAfter: 0,
    errorMessage: '',
  });

  // Load coach data — skip fetch when pre-fetched data is provided
  useEffect(() => {
    const loadCoachData = async () => {
      if (coachProp) {
        // Use pre-fetched data — skip redundant Firestore reads
        setCoach(coachProp);
        setCoachId(coachIdProp || coachProp.id);
        if (initialUsername) setUsername(initialUsername);
        if (initialPlayerProfile?.photoUrl) setProfilePhoto(initialPlayerProfile.photoUrl);
        setLoading(false);
        return;
      }

      if (!profileId) {
        setLoading(false);
        return;
      }

      try {
        // Get username and player profile photo
        const [fetchedUsername, profileData] = await Promise.all([
          getUsernameFromProfileId(profileId),
          getPlayerProfile(profileId),
        ]);
        setUsername(fetchedUsername);
        if (profileData?.photoUrl) {
          setProfilePhoto(profileData.photoUrl);
        }

        // Get coach profile by playerProfileId
        const coachData = await getCoachByPlayerProfile(profileId);

        if (!coachData) {
          setLoading(false);
          return;
        }

        setCoachId(coachData.id);
        setCoach(coachData);
      } catch (error) {
        console.error('Error loading coach data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCoachData();
  }, [profileId, coachProp]);

  // Load lessons after we have coachId
  useEffect(() => {
    const loadLessons = async () => {
      if (!coachId) {
        setIsLoadingLessons(false);
        return;
      }

      setIsLoadingLessons(true);
      try {
        const [upcomingLessons, upcomingIndividual] = await Promise.all([
          getUpcomingLessons(coachId, 10),
          getUpcomingIndividualSessions(coachId, 10),
        ]);

        const combined = [...upcomingLessons, ...upcomingIndividual];
        combined.sort((a, b) => {
          const dateA = a.scheduledAtUTC?.toDate?.()
            || (a.slots?.find(s => s.startTime)
              ? new Date(`${a.date}T${a.slots.find(s => s.startTime).startTime}:00`)
              : new Date(a.date + 'T00:00:00'));
          const dateB = b.scheduledAtUTC?.toDate?.()
            || (b.slots?.find(s => s.startTime)
              ? new Date(`${b.date}T${b.slots.find(s => s.startTime).startTime}:00`)
              : new Date(b.date + 'T00:00:00'));
          const diff = dateA - dateB;
          if (diff !== 0) return diff;
          const typeA = a.scheduledAtUTC ? 'group' : 'individual';
          const typeB = b.scheduledAtUTC ? 'group' : 'individual';
          if (typeA === 'group' && typeB === 'individual') return -1;
          if (typeA === 'individual' && typeB === 'group') return 1;
          return 0;
        });
        setLessons(combined);
      } catch (error) {
        console.error('Error loading lessons:', error);
        setLessons([]);
      } finally {
        setIsLoadingLessons(false);
      }
    };

    loadLessons();
  }, [coachId]);

  // Load user enrollment/booking state when logged in
  useEffect(() => {
    if (!coachId || !currentUser?.uid) {
      setEnrolledLessonIds([]);
      return;
    }

    const loadUserState = async () => {
      try {
        const [enrolled, credits, membershipData] = await Promise.all([
          getEnrolledLessons(coachId, currentUser.uid),
          getUserCoachCredits(currentUser.uid, coachId),
          getUserMembershipCredits(currentUser.uid, coachId),
        ]);
        setEnrolledLessonIds(enrolled.map(l => l.id));
        setUserCredits(credits || 0);
        if (membershipData) {
          setIsMember(!!membershipData.isMember);
          setFreeTrialClaimed(!!membershipData.freeTrialClaimed);
        }
      } catch (error) {
        console.error('Error loading user enrollment state:', error);
      }
    };

    loadUserState();
  }, [coachId, currentUser?.uid]);

  // Get hero content from coach or defaults
  const getHeroContent = () => {
    if (coach) {
      return {
        title: coach.heroTitle || 'Learn Chess With Me',
        subtitle: coach.heroDescription || 'Interactive group lessons for players ready to improve.',
        image: coach.heroImage || profilePhoto || null,
      };
    }
    return {
      title: 'Learn Chess With Me',
      subtitle: 'Interactive group lessons for players ready to improve.',
      image: profilePhoto || null,
    };
  };

  // Get package pricing from coach or defaults
  const getPackages = () => {
    if (coach?.packagePricing) {
      const pricing = coach.packagePricing;
      return [
        {
          name: '1 Session',
          price: Number(pricing.sessions1?.usd) || 15,
          credits: 1,
          perCredit: Number(pricing.sessions1?.usd) || 15,
          perk: pricing.sessions1?.perk || '',
          popular: false,
        },
        {
          name: '4 Sessions',
          price: Number(pricing.sessions4?.usd) || 52,
          credits: 4,
          perCredit: (Number(pricing.sessions4?.usd) || 52) / 4,
          perk: pricing.sessions4?.perk || '',
          popular: true,
        },
        {
          name: '7 Sessions',
          price: Number(pricing.sessions7?.usd) || 84,
          credits: 7,
          perCredit: (Number(pricing.sessions7?.usd) || 84) / 7,
          perk: pricing.sessions7?.perk || '',
          popular: false,
        },
      ];
    }
    return [
      { name: '1 Session', price: 15, credits: 1, perCredit: 15, perk: '', popular: false },
      { name: '4 Sessions', price: 52, credits: 4, perCredit: 13, perk: '', popular: true },
      { name: '7 Sessions', price: 84, credits: 7, perCredit: 12, perk: '', popular: false },
    ];
  };

  const heroContent = getHeroContent();
  const packages = getPackages();
  const baseUrl = username ? `/coach/${username}` : '/coach';

  // Membership helpers
  const hasMembershipConfig = coach?.membership?.enabled && coach?.membership?.stripePriceId;
  const membershipConfig = hasMembershipConfig ? coach.membership : null;

  // Offer visibility flags (base config)
  const showMembership = !!hasMembershipConfig;
  const showCreditPacks = coach?.offers?.creditPacksPublic !== false;
  const showFreeTrial = coach?.offers?.freeTrialLesson === true;

  // Determine if current user is the coach (owner)
  const isOwner = !!(currentUser?.uid && coach?.userId && currentUser.uid === coach.userId);

  // Membership state exposed by InlineMembershipCard via callback
  const [isMember, setIsMember] = useState(false);
  const [freeTrialClaimed, setFreeTrialClaimed] = useState(false);

  const handleMembershipLoaded = ({ isMember: memberStatus, freeTrialClaimed: trialClaimed }) => {
    setIsMember(memberStatus);
    setFreeTrialClaimed(trialClaimed);
  };

  // Membership gating
  const lessonsRequireMembership = coach?.membership?.lessonsRequireMembership === true;

  // Contextual pricing visibility based on user state
  const showMembershipCard = showMembership && !isMember;
  const showFreeTrialBtn = showFreeTrial && (isOwner || (!freeTrialClaimed && userCredits === 0 && !isMember));
  const showPricingSection = showMembershipCard || showFreeTrialBtn || showCreditPacks;

  // Smart CTA routing for pricing cards
  const handleGetStarted = (packageCredits) => {
    trackEvent('Coaching CTA Click', { coach: username, package: `${packageCredits} Sessions` });

    if (!currentUser) {
      window.location.href = `/auth?context=student&redirect=/coach/${username}/checkout?package=${packageCredits}`;
      return;
    }

    if (userCredits > 0) {
      window.location.href = `/coach/${username}/dashboard`;
      return;
    }

    window.location.href = `/coach/${username}/checkout?package=${packageCredits}`;
  };

  // Close the credit action modal
  const closeModal = () => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  };

  // Handle inline registration for group lessons (credit-based)
  const handleRegister = async (lessonId) => {
    if (!currentUser || isRegistering || !coachId) return;

    const lesson = lessons.find(l => l.id === lessonId);
    const lessonTitle = lesson?.title || 'this lesson';

    setIsRegistering(true);

    // Show loading modal
    setModalState({
      isOpen: true,
      type: 'loading',
      action: 'booking',
      lessonTitle,
      creditsBefore: userCredits,
      creditsAfter: userCredits - 1,
      errorMessage: '',
    });

    try {
      // Fresh credit check from DB (prevents stale state issues)
      const currentCredits = await getUserCoachCredits(currentUser.uid, coachId);
      setUserCredits(currentCredits);

      if (currentCredits <= 0) {
        // No credits — redirect to checkout
        closeModal();
        window.location.href = `${baseUrl}/checkout?lessonId=${lessonId}`;
        return;
      }

      const creditsBefore = currentCredits;
      const creditsAfter = currentCredits - 1;

      const participantData = {
        odaUserId: currentUser.uid,
        email: currentUser.email || '',
        name: currentUser.displayName || currentUser.email?.split('@')[0] || 'Student',
        creditsRemaining: creditsAfter,
      };

      await registerForLesson(lessonId, participantData);
      trackEvent('Lesson Registration', { coach: username, lessonType: 'group' });

      // Optimistically update enrollment
      setEnrolledLessonIds(prev => [...prev, lessonId]);
      setUserCredits(creditsAfter);

      // Show success modal
      setModalState({
        isOpen: true,
        type: 'success',
        action: 'booking',
        lessonTitle,
        creditsBefore,
        creditsAfter,
        errorMessage: '',
      });
    } catch (error) {
      console.error('Error registering for lesson:', error);
      setModalState({
        isOpen: true,
        type: 'error',
        action: 'booking',
        lessonTitle,
        creditsBefore: userCredits,
        creditsAfter: userCredits,
        errorMessage: error.message || 'Failed to register for lesson. Please try again.',
      });
    } finally {
      setIsRegistering(false);
    }
  };

  // If not coaching enabled, show placeholder
  if (!loading && !coach) {
    return (
      <div className="coaching-block-placeholder" style={cssVars}>
        <div className="placeholder-icon">
          <GraduationCap size={48} />
        </div>
        <h3>Coaching Not Set Up</h3>
        <p>Set up coaching in your admin panel to display your lessons and pricing here.</p>
        <style>{`
          .coaching-block-placeholder {
            padding: 60px 24px;
            text-align: center;
            background: var(--cb-section-bg);
            border-radius: 16px;
            border: 2px dashed rgba(var(--cb-accent-rgb), 0.3);
          }
          .placeholder-icon {
            width: 80px;
            height: 80px;
            margin: 0 auto 20px;
            background: rgba(var(--cb-accent-rgb), 0.1);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: var(--cb-accent);
          }
          .coaching-block-placeholder h3 {
            font-size: 1.25rem;
            font-weight: 600;
            color: var(--cb-heading-color);
            margin-bottom: 8px;
            font-family: var(--cb-heading-font);
          }
          .coaching-block-placeholder p {
            color: var(--cb-muted-color);
            font-size: 0.95rem;
            font-family: var(--cb-body-font);
          }
        `}</style>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="coaching-block-loading" style={cssVars}>
        <div className="loading-spinner"></div>
        <span>Loading coaching...</span>
        <style>{`
          .coaching-block-loading {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 80px 24px;
            color: var(--cb-muted-color);
            font-family: var(--cb-body-font);
          }
          .loading-spinner {
            width: 40px;
            height: 40px;
            border: 3px solid rgba(var(--cb-accent-rgb), 0.2);
            border-top-color: var(--cb-accent);
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin-bottom: 16px;
          }
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="coaching-block" style={cssVars}>
      <div id="coaching-lessons-top" />
      {/* Extra spacing when embedded in /me page so carousel clears the header */}
      {isEmbedded && <div style={{ height: 64 }} />}
      {/* Lesson Carousel Section */}
      <LessonCarousel
        lessons={lessons}
        loading={isLoadingLessons}
        currentUser={currentUser}
        userCredits={userCredits}
        enrolledLessonIds={enrolledLessonIds}
        onRegister={handleRegister}
      />

      {lessons.length > 0 && (
        <div className="how-it-works-strip mb-4">
          <div className="how-it-works-item">
            <Video size={18} />
            <span>Live video sessions included with every lesson</span>
          </div>
          <div className="how-it-works-item">
            <Mail size={18} />
            <span>Email confirmations & reminders before each lesson</span>
          </div>
          <div className="how-it-works-item">
            <MonitorPlay size={18} />
            <span>Manage everything from your{' '}
              <a
                href={currentUser ? `/coach/${username}/dashboard` : `/auth?context=student&redirect=/coach/${username}/dashboard`}
                style={{ color: '#2563eb', textDecoration: 'underline', fontWeight: 600 }}
              >Student Dashboard</a>
            </span>
          </div>
        </div>
      )}

      {/* Show More Button - Only shown when embedded and collapsed */}
      {isEmbedded && !isExpanded && (
        <div className="show-more-container">
          <button className="show-more-btn" onClick={() => setIsExpanded(true)}>
            <span>Show More</span>
            <ChevronDown size={20} />
          </button>
        </div>
      )}

      {/* Collapsible Content - Hero, Pricing, FAQ, CTA */}
      {isExpanded && (
        <>
          {/* Hero Section */}
          <section className="gc-hero">
            <div className="hero-bg">
              <div className="hero-gradient"></div>
              <div className="hero-pattern"></div>
            </div>
            <div className="container hero-container">
              <div className="hero-content">
                <div className="hero-text">
                  <h1>{heroContent.title}</h1>
                  <p className="hero-subtitle">{heroContent.subtitle}</p>
                </div>
              </div>
            </div>
          </section>

          {/* Pricing Section — unified membership + credit packs */}
          {showPricingSection && (
          <section className="pricing-section">
            <div className="container">
              {showMembershipCard && <h2 className="section-heading">Choose Your Plan</h2>}

              {/* Membership card — featured option when coach has membership */}
              {showMembershipCard && membershipConfig && (
                <InlineMembershipCard
                  coach={coach}
                  coachId={coachId}
                  username={username}
                  currentUser={currentUser}
                  isOwner={isOwner}
                  onMembershipLoaded={handleMembershipLoaded}
                />
              )}

              {/* Credit packs — secondary a la carte option */}
              {showCreditPacks && (
              <div className="credit-packs-section ">
                {showMembershipCard && (
                  <p className="credit-packs-divider p-6 pb-10">Or buy group lesson credits in a bundle.</p>
                )}

                {/* Membership gating messaging */}
                {lessonsRequireMembership && !isMember && !isOwner && (
                  <div className="membership-gate-banner">
                    <Lock size={16} />
                    <span>Subscribe to unlock lesson booking</span>
                  </div>
                )}
                {lessonsRequireMembership && (isMember || isOwner) && (
                  <p className="pricing-section-heading">Need more credits?</p>
                )}

                <div className={`hero-pricing${lessonsRequireMembership && !isMember && !isOwner ? ' pricing-gated' : ''}`}>
                  {packages.map((pack) => (
                    <div key={pack.name} className={`pricing-card ${pack.popular ? 'popular' : ''}`}>
                      {pack.popular && (
                        <span className="popular-tag">
                          <Sparkles size={12} />
                          Popular
                        </span>
                      )}
                      <div className="pricing-amount">
                        <span className="currency">$</span>
                        <span className="price">{pack.price}</span>
                      </div>
                      <div className="pricing-credits">{pack.credits} {pack.credits === 1 ? 'session' : 'sessions'}</div>
                      <div className="pricing-per">${pack.perCredit.toFixed(2)}/session</div>
                      {pack.perk && <div className="pricing-perk">{pack.perk}</div>}
                      <button
                        className="pricing-btn"
                        onClick={() => handleGetStarted(pack.credits)}
                        disabled={lessonsRequireMembership && !isMember && !isOwner}
                      >
                        Get Started {pack.credits !== 7 && <ArrowRight size={16} />}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              )}

              {/* Free Trial Button — below pricing */}
              {showFreeTrialBtn && (
                <InlineFreeTrialButton coach={coach} coachId={coachId} username={username} isOwner={isOwner} />
              )}
            </div>
          </section>
          )}

          {/* FAQ Section */}
          <section id="faq" className="faq-landing-section">
            <div className="container">
              <h2 className="section-heading">Frequently Asked Questions</h2>
              <p className="section-subheading">Everything you need to know about classes, credits, and getting started.</p>
              {/* Coach custom FAQs render above platform FAQs */}
              {coach?.customFaq?.length > 0 && (
                <div style={{ marginBottom: '10px' }}>
                  <FaqAccordion faqItems={coach.customFaq} className="custom-faqs" />
                </div>
              )}
              <FaqAccordion faqItems={defaultFaq} />
            </div>
          </section>

          {/* Bottom CTA Section */}
          <section className="bottom-cta-section">
            <div className="container">
              <div className="bottom-cta-card">
                <h2>Ready to Book a Lesson?</h2>
                <p>Browse the schedule and sign up for your next class.</p>
                <button className="bottom-cta-btn" onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}>
                  View Schedule <ArrowRight size={18} />
                </button>
              </div>
            </div>
          </section>
        </>
      )}

      {/* Credit Action Modal */}
      <CreditActionModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        type={modalState.type}
        action={modalState.action}
        lessonTitle={modalState.lessonTitle}
        creditsBefore={modalState.creditsBefore}
        creditsAfter={modalState.creditsAfter}
        errorMessage={modalState.errorMessage}
      />

      <style>{`
        .coaching-block {
          background: var(--cb-section-bg);
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px;
        }

        /* ── Font injection ─────────────────────────────────────── */
        .hero-text h1,
        .section-heading,
        .bottom-cta-card h2 { font-family: var(--cb-heading-font); }

        .hero-subtitle,
        .section-subheading,
        .bottom-cta-card p,
        .faq-answer p { font-family: var(--cb-body-font); }

        /* Video Info Line */
        .how-it-works-strip {
          display: flex;
          justify-content: center;
          gap: 32px;
          padding: 16px 24px;
          font-family: var(--cb-body-font);
          font-size: 0.85rem;
          color: var(--cb-muted-color);
          flex-wrap: wrap;
        }

        .how-it-works-item {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .how-it-works-item svg {
          flex-shrink: 0;
          opacity: 0.7;
        }

        /* Show More Button */
        .show-more-container {
          display: flex;
          justify-content: center;
          padding: calc(24px * var(--cb-padding-scale)) 0 calc(48px * var(--cb-padding-scale));
        }

        .show-more-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 28px;
          background: rgba(var(--cb-accent-rgb), 0.15);
          border: 1px solid rgba(var(--cb-accent-rgb), 0.4);
          border-radius: 30px;
          color: var(--cb-accent-light);
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          font-family: var(--cb-body-font);
        }

        .show-more-btn:hover {
          background: rgba(var(--cb-accent-rgb), 0.25);
          border-color: var(--cb-accent);
          transform: translateY(-2px);
        }

        .show-more-btn svg {
          transition: transform 0.2s ease;
        }

        .show-more-btn:hover svg {
          transform: translateY(2px);
        }

        /* Hero Section */
        .gc-hero {
          position: relative;
          padding: calc(60px * var(--cb-padding-scale)) 0;
          overflow: hidden;
          display: flex;
          align-items: center;
        }

        .hero-bg {
          position: absolute;
          inset: 0;
          z-index: 0;
        }

        .hero-gradient {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(var(--cb-accent-rgb), 0.15) 0%, rgba(var(--cb-accent-rgb), 0.1) 50%, transparent 100%);
        }

        .hero-pattern {
          position: absolute;
          inset: 0;
          background-image: radial-gradient(circle at 1px 1px, rgba(var(--cb-heading-rgb), 0.05) 1px, transparent 1px);
          background-size: 40px 40px;
          opacity: 0.5;
        }

        .hero-container {
          position: relative;
          z-index: 1;
        }

        .hero-content {
          text-align: var(--cb-heading-align);
          max-width: 1100px;
          margin: 0 auto;
        }

        .hero-text h1 {
          font-size: calc(clamp(2.5rem, 5vw, 3.5rem) * var(--cb-heading-scale));
          font-weight: 800;
          color: var(--cb-heading-color);
          line-height: 1.15;
          margin-bottom: 24px;
        }

        .hero-subtitle {
          font-size: 1.25rem;
          color: var(--cb-muted-color);
          line-height: 1.6;
          margin-bottom: 0;
          max-width: 700px;
          margin-left: auto;
          margin-right: auto;
        }

        /* Pricing Section */
        .pricing-section {
          padding: calc(60px * var(--cb-padding-scale)) 0;
        }

        .pricing-section .section-heading {
          margin-bottom: 32px;
        }

        .pricing-membership-featured {
          display: flex;
          justify-content: center;
          margin-bottom: 32px;
        }

        .cr-membership-card {
          position: relative;
          background: var(--cb-card-bg);
          border: 2px solid var(--cb-accent);
          border-radius: 16px;
          padding: 32px 28px 28px;
          max-width: 520px;
          width: 100%;
          text-align: center;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.03);
        }

        .cr-membership-recommended-tag {
          position: absolute;
          top: -12px;
          left: 50%;
          transform: translateX(-50%);
          background: linear-gradient(135deg, var(--cb-accent), var(--cb-accent-dark));
          color: var(--cb-accent-text, white);
          padding: 5px 16px;
          border-radius: 12px;
          font-size: 0.72rem;
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 5px;
          white-space: nowrap;
          letter-spacing: 0.02em;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
        }

        .cr-membership-header {
          display: flex;
          align-items: center;
          gap: 14px;
          margin-bottom: 16px;
          text-align: left;
        }

        .cr-membership-crown {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          background: linear-gradient(135deg, var(--cb-accent), var(--cb-accent-dark));
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--cb-accent-text, white);
          flex-shrink: 0;
        }

        .cr-membership-card h3 {
          margin: 0;
          font-size: 1rem;
          font-weight: 700;
          color: var(--cb-heading-color);
          font-family: var(--cb-heading-font);
        }

        .cr-membership-price {
          display: flex;
          align-items: baseline;
          gap: 2px;
        }

        .cr-membership-currency {
          font-size: 1rem;
          font-weight: 600;
          color: var(--cb-heading-color);
        }

        .cr-membership-amount {
          font-size: 1.75rem;
          font-weight: 800;
          color: var(--cb-heading-color);
          line-height: 1;
        }

        .cr-membership-period {
          font-size: 0.85rem;
          color: var(--cb-muted-color);
          margin-left: 2px;
        }

        .cr-membership-perks {
          list-style: none;
          padding: 0;
          margin: 0 0 24px;
          text-align: left;
        }

        .cr-membership-perks li {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          padding: 6px 0;
          font-size: 0.9rem;
          color: var(--cb-heading-color);
          font-family: var(--cb-body-font);
        }

        .cr-membership-perks li svg {
          color: var(--cb-accent);
          flex-shrink: 0;
          margin-top: 2px;
        }

        .cr-membership-cta {
          width: 100%;
          padding: 14px 28px;
          background: linear-gradient(135deg, var(--cb-accent), var(--cb-accent-dark));
          color: var(--cb-accent-text, white);
          border: none;
          border-radius: 10px;
          font-size: 1.05rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-top: 4px;
        }

        .cr-membership-cta:hover:not(:disabled) {
          transform: translateY(-2px);
          filter: brightness(1.1);
          box-shadow: 0 6px 20px rgba(var(--cb-accent-rgb), 0.4);
        }

        .cr-membership-cta:disabled {
          opacity: 0.6;
          cursor: default;
        }

        .cr-membership-card.cr-membership-owner {
          border-style: dashed;
        }

        .cr-membership-owner-badge {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 10px 16px;
          background: var(--cb-card-bg);
          border: 1px dashed var(--cb-border-color);
          border-radius: 10px;
          color: var(--cb-muted-color);
          font-size: 0.85rem;
          font-family: var(--cb-body-font);
        }

        .cr-membership-active-badge {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px 16px;
          background: var(--cb-card-bg);
          border: 1px solid var(--cb-border-color);
          border-radius: 10px;
          color: var(--cb-accent);
          font-weight: 600;
          font-size: 0.9rem;
          flex-wrap: wrap;
          font-family: var(--cb-body-font);
        }

        .cr-membership-renew {
          font-size: 0.78rem;
          font-weight: 400;
          color: var(--cb-muted-color);
          width: 100%;
          text-align: center;
          margin-top: 2px;
        }

        .credit-packs-divider {
          text-align: center;
          font-size: 0.95rem;
          font-weight: 600;
          color: var(--cb-muted-color);
          margin-bottom: 20px;
          font-family: var(--cb-body-font);
        }

        /* Membership gating styles */
        .membership-gate-banner {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px 20px;
          background: rgba(var(--cb-accent-rgb), 0.1);
          border: 1px solid rgba(var(--cb-accent-rgb), 0.25);
          border-radius: 10px;
          color: var(--cb-accent);
          font-size: 0.9rem;
          font-weight: 600;
          margin-bottom: 20px;
          max-width: 400px;
          margin-left: auto;
          margin-right: auto;
          font-family: var(--cb-body-font);
        }

        .pricing-section-heading {
          text-align: center;
          font-size: 1rem;
          font-weight: 600;
          color: var(--cb-muted-color);
          margin-bottom: 16px;
          font-family: var(--cb-body-font);
        }

        .hero-pricing.pricing-gated {
          opacity: 0.5;
          pointer-events: none;
        }

        .hero-pricing.pricing-gated .pricing-btn {
          opacity: 0.4;
        }

        .pricing-note {
          text-align: center;
          margin-top: 24px;
          font-size: 0.85rem;
          color: var(--cb-muted-color);
          font-family: var(--cb-body-font);
        }

        .spin {
          animation: spin 1s linear infinite;
        }

        .hero-pricing {
          display: flex;
          gap: 16px;
          justify-content: center;
          align-items: center;
          flex-wrap: wrap;
        }

        .pricing-card {
          background: var(--cb-card-bg);
          border: 1px solid var(--cb-border-color);
          border-radius: 16px;
          padding: 24px 16px;
          min-width: 180px;
          width: 200px;
          min-height: 240px;
          text-align: center;
          position: relative;
          transition: all 0.2s ease;
          display: flex;
          flex-direction: column;
        }

        .pricing-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
          border-color: rgba(var(--cb-accent-rgb), 0.3);
        }

        .pricing-card.popular {
          border-color: var(--cb-accent);
          background: linear-gradient(to bottom, rgba(var(--cb-accent-rgb), 0.2), var(--cb-card-bg));
          transform: scale(1.08);
          min-height: 270px;
          z-index: 1;
          box-shadow: 0 10px 40px rgba(var(--cb-accent-rgb), 0.2);
        }

        .pricing-card.popular:hover {
          transform: scale(1.08) translateY(-4px);
        }

        .popular-tag {
          position: absolute;
          top: -10px;
          left: 50%;
          transform: translateX(-50%);
          background: linear-gradient(135deg, var(--cb-accent) 0%, var(--cb-accent-dark) 100%);
          color: var(--cb-accent-text, white);
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 0.7rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 4px;
          white-space: nowrap;
        }

        .pricing-amount {
          display: flex;
          align-items: flex-start;
          justify-content: center;
          margin-bottom: 4px;
        }

        .pricing-amount .currency {
          font-size: 1rem;
          font-weight: 600;
          color: var(--cb-heading-color);
          margin-top: 4px;
        }

        .pricing-amount .price {
          font-size: 2.25rem;
          font-weight: 800;
          color: var(--cb-heading-color);
          line-height: 1;
        }

        .pricing-credits {
          font-size: 0.95rem;
          font-weight: 600;
          color: var(--cb-accent-light);
          margin-bottom: 2px;
        }

        .pricing-per {
          font-size: 0.8rem;
          color: var(--cb-muted-color);
          margin-bottom: 8px;
        }

        .pricing-perk {
          font-size: 0.8rem;
          color: var(--cb-accent-light);
          font-weight: 500;
          padding: 6px 12px;
          background: rgba(var(--cb-accent-rgb), 0.1);
          border-radius: 6px;
          margin-bottom: 12px;
          text-align: left;
          display: flex;
          align-items: flex-start;
          gap: 6px;
        }

        .pricing-perk::before {
          content: "•";
          flex-shrink: 0;
        }

        .pricing-btn {
          width: 100%;
          padding: 10px 20px;
          background: transparent;
          color: var(--cb-accent-light);
          border: 1px solid var(--cb-accent-light);
          border-radius: 8px;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-top: auto;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          text-decoration: none;
        }

        .pricing-btn:hover {
          background: var(--cb-accent);
          color: var(--cb-accent-text, white);
          border-color: var(--cb-accent);
        }

        .pricing-card.popular .pricing-btn {
          background: linear-gradient(135deg, var(--cb-accent) 0%, var(--cb-accent-dark) 100%);
          color: var(--cb-accent-text, white);
          border: none;
        }

        .pricing-card.popular .pricing-btn:hover {
          box-shadow: 0 4px 12px rgba(var(--cb-accent-rgb), 0.4);
        }

        /* FAQ Section */
        .faq-landing-section {
          padding: calc(80px * var(--cb-padding-scale)) 0;
        }

        .section-heading {
          font-size: calc(clamp(1.75rem, 4vw, 2.25rem) * var(--cb-heading-scale));
          font-weight: 700;
          color: var(--cb-heading-color);
          text-align: var(--cb-heading-align);
          margin-bottom: 12px;
        }

        .section-subheading {
          font-size: 1.1rem;
          color: var(--cb-muted-color);
          text-align: var(--cb-heading-align);
          margin-bottom: 40px;
          max-width: 500px;
          margin-left: auto;
          margin-right: auto;
        }

        /* Bottom CTA Section */
        .bottom-cta-section {
          padding: calc(60px * var(--cb-padding-scale)) 0 calc(80px * var(--cb-padding-scale));
        }

        .bottom-cta-card {
          background: linear-gradient(135deg, rgba(var(--cb-accent-rgb), 0.2) 0%, rgba(var(--cb-accent-rgb), 0.05) 100%);
          border: 1px solid rgba(var(--cb-accent-rgb), 0.3);
          border-radius: 20px;
          padding: calc(48px * var(--cb-padding-scale)) 32px;
          text-align: var(--cb-heading-align);
          max-width: 600px;
          margin: 0 auto;
        }

        .bottom-cta-card h2 {
          font-size: calc(1.75rem * var(--cb-heading-scale));
          font-weight: 700;
          color: var(--cb-heading-color);
          margin-bottom: 12px;
        }

        .bottom-cta-card p {
          font-size: 1.1rem;
          color: var(--cb-muted-color);
          margin-bottom: 24px;
        }

        .bottom-cta-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 14px 32px;
          background: var(--cb-btn-bg);
          color: var(--cb-btn-color);
          border: var(--cb-btn-border);
          border-radius: 10px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          text-decoration: none;
          gap: 8px;
        }

        .bottom-cta-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(var(--cb-accent-rgb), 0.4);
        }

        /* Responsive */
        @media (max-width: 768px) {
          .gc-hero {
            padding: calc(40px * var(--cb-padding-scale)) 0;
          }

          .hero-pricing {
            flex-direction: column;
            align-items: center;
          }

          .pricing-card {
            width: 100%;
            max-width: 280px;
          }

          .pricing-card.popular {
            transform: scale(1);
            min-height: 240px;
          }

          .pricing-card.popular:hover {
            transform: translateY(-4px);
          }

          .faq-landing-section {
            padding: calc(60px * var(--cb-padding-scale)) 0;
          }

          .bottom-cta-section {
            padding: calc(40px * var(--cb-padding-scale)) 0 calc(60px * var(--cb-padding-scale));
          }

          .bottom-cta-card {
            padding: calc(36px * var(--cb-padding-scale)) 24px;
          }

          .bottom-cta-card h2 {
            font-size: calc(1.5rem * var(--cb-heading-scale));
          }
        }
      `}</style>
    </div>
  );
};


/**
 * FAQ Accordion component
 */
const FaqAccordion = ({ faqItems, className = '' }) => {
  const [openId, setOpenId] = useState(null);

  return (
    <div className={`faq-section ${className}`}>
      <div className="faq-list">
        {faqItems.map((item, index) => (
          <div key={index} className={`faq-item ${openId === index ? 'open' : ''}`}>
            <button
              className="faq-question"
              onClick={() => setOpenId(openId === index ? null : index)}
            >
              <span>{item.question}</span>
              <ChevronDown size={18} className={openId === index ? 'rotated' : ''} />
            </button>
            {openId === index && (
              <div className="faq-answer">
                <p>{item.answer}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      <style>{`
        .faq-section {
          width: 100%;
          max-width: 700px;
          margin: 0 auto;
        }

        .faq-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .faq-item {
          background: var(--cb-card-bg);
          border: 1px solid var(--cb-border-color);
          border-radius: 12px;
          overflow: hidden;
          transition: all 0.2s ease;
        }

        .faq-item:hover {
          border-color: rgba(var(--cb-accent-rgb), 0.5);
        }

        .faq-item.open {
          border-color: var(--cb-accent);
        }

        .faq-question {
          width: 100%;
          padding: 16px 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
          background: none;
          border: none;
          cursor: pointer;
          text-align: left;
          font-size: 0.95rem;
          font-weight: 500;
          color: var(--cb-heading-color);
          font-family: var(--cb-body-font);
        }

        .faq-question svg {
          color: var(--cb-accent-light);
          flex-shrink: 0;
          transition: transform 0.2s ease;
        }

        .faq-question svg.rotated {
          transform: rotate(180deg);
        }

        .faq-answer {
          padding: 0 20px 16px;
        }

        .faq-answer p {
          font-size: 0.95rem;
          color: var(--cb-muted-color);
          line-height: 1.6;
        }

        .custom-faqs .faq-item {
          border-left: 3px solid var(--cb-accent);
          background: linear-gradient(90deg, rgba(var(--cb-accent-rgb), 0.06) 0%, var(--cb-card-bg) 40%);
        }
      `}</style>
    </div>
  );
};

/**
 * Inline Free Trial Button for the coaching block
 * Self-contained: fetches its own freeTrialClaimed state (no CoachContext dependency)
 */
const InlineFreeTrialButton = ({ coach, coachId, username, isOwner }) => {
  const { currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [checkingClaimed, setCheckingClaimed] = useState(true);
  const [error, setError] = useState('');
  const autoClaimTriggered = useRef(false);

  useEffect(() => {
    if (!currentUser?.uid || !coachId) {
      setCheckingClaimed(false);
      return;
    }

    let cancelled = false;
    getUserMembershipCredits(currentUser.uid, coachId)
      .then((info) => {
        if (!cancelled && info.freeTrialClaimed) {
          setClaimed(true);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setCheckingClaimed(false);
      });

    return () => { cancelled = true; };
  }, [currentUser?.uid, coachId]);

  // Auto-trigger claim after auth redirect
  useEffect(() => {
    if (
      currentUser &&
      searchParams.get('autoCheckout') === 'freeTrial' &&
      !autoClaimTriggered.current
    ) {
      autoClaimTriggered.current = true;
      searchParams.delete('autoCheckout');
      setSearchParams(searchParams, { replace: true });
      handleClaim();
    }
  }, [currentUser, searchParams]);

  const handleClaim = async () => {
    if (!currentUser) {
      window.location.href = `/auth?context=student&redirect=${encodeURIComponent(`/coach/${username}?autoCheckout=freeTrial`)}`;
      return;
    }

    setLoading(true);
    setError('');
    try {
      await claimFreeTrialLesson(coachId);
      setClaimed(true);
    } catch (err) {
      if (err.message?.includes('already claimed')) {
        setClaimed(true);
      } else {
        setError(err.message || 'Failed to claim free trial');
      }
    } finally {
      setLoading(false);
    }
  };

  if (checkingClaimed) return null;

  // Owner always sees the button as a preview of what students see
  if (isOwner) {
    return (
      <div className="cr-free-trial-wrapper">
        <button className="cr-free-trial-btn" disabled>
          <Gift size={16} /> Try a Free Group Lesson
        </button>
        <p className="cr-free-trial-owner-note">This is what your students see</p>
        <style>{inlineFreeTrialStyles}</style>
      </div>
    );
  }

  if (claimed) {
    return (
      <div className="cr-free-trial-wrapper">
        <div className="cr-free-trial-claimed">
          <Check size={16} /> Free trial claimed
        </div>
        <style>{inlineFreeTrialStyles}</style>
      </div>
    );
  }

  return (
    <div className="cr-free-trial-wrapper">
      <button
        className="cr-free-trial-btn"
        onClick={handleClaim}
        disabled={loading}
      >
        {loading ? (
          <><Loader2 size={16} className="spin" /> Claiming...</>
        ) : (
          <><Gift size={16} /> Try a Free Group Lesson</>
        )}
      </button>
      {error && <p className="cr-free-trial-error">{error}</p>}
      <style>{inlineFreeTrialStyles}</style>
    </div>
  );
};

const inlineFreeTrialStyles = `
  .cr-free-trial-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 56px 0 24px;
  }

  .cr-free-trial-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 28px;
    background: transparent;
    color: var(--cb-accent);
    border: 2px solid var(--cb-accent);
    border-radius: 10px;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    font-family: var(--cb-body-font);
  }

  .cr-free-trial-btn:hover:not(:disabled) {
    background: rgba(var(--cb-accent-rgb), 0.1);
    transform: translateY(-1px);
  }

  .cr-free-trial-btn:disabled {
    opacity: 0.6;
    cursor: default;
  }

  .cr-free-trial-claimed {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: rgba(34, 197, 94, 0.1);
    border: 1px solid rgba(34, 197, 94, 0.3);
    border-radius: 10px;
    color: #22c55e;
    font-size: 0.9rem;
    font-weight: 500;
    font-family: var(--cb-body-font);
  }

  .cr-free-trial-error {
    margin-top: 8px;
    font-size: 0.8rem;
    color: #ef4444;
    font-family: var(--cb-body-font);
  }

  .cr-free-trial-owner-note {
    margin-top: 8px;
    font-size: 0.8rem;
    color: var(--cb-text-muted, #999);
    font-style: italic;
    font-family: var(--cb-body-font);
  }
`;

/**
 * Inline Membership Card for the coaching block
 * Self-contained: fetches its own membership status (no CoachContext dependency)
 * 3 states: owner preview, active member, subscribe (non-member)
 * Auto-checkout after auth redirect via ?membership_checkout=true
 */
const InlineMembershipCard = ({ coach, coachId, username, currentUser, isOwner, onMembershipLoaded }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [isMember, setIsMember] = useState(false);
  const [membershipCurrentPeriodEnd, setMembershipCurrentPeriodEnd] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const autoCheckoutTriggered = useRef(false);

  const membershipConfig = coach?.membership;

  // Self-fetch membership status
  useEffect(() => {
    if (!currentUser?.uid || !coachId) {
      setCheckingStatus(false);
      onMembershipLoaded?.({ isMember: false, freeTrialClaimed: false });
      return;
    }

    let cancelled = false;
    getUserMembershipCredits(currentUser.uid, coachId)
      .then((info) => {
        if (!cancelled) {
          setIsMember(!!info.isMember);
          setMembershipCurrentPeriodEnd(info.membershipCurrentPeriodEnd || null);
          onMembershipLoaded?.({ isMember: !!info.isMember, freeTrialClaimed: !!info.freeTrialClaimed });
        }
      })
      .catch(() => {
        if (!cancelled) {
          onMembershipLoaded?.({ isMember: false, freeTrialClaimed: false });
        }
      })
      .finally(() => {
        if (!cancelled) setCheckingStatus(false);
      });

    return () => { cancelled = true; };
  }, [currentUser?.uid, coachId]);

  // Auto-trigger checkout after auth redirect
  useEffect(() => {
    if (
      currentUser &&
      searchParams.get('membership_checkout') === 'true' &&
      !autoCheckoutTriggered.current
    ) {
      autoCheckoutTriggered.current = true;
      searchParams.delete('membership_checkout');
      setSearchParams(searchParams, { replace: true });
      handleSubscribe();
    }
  }, [currentUser, searchParams]);

  const handleSubscribe = async () => {
    if (!currentUser) {
      window.location.href = `/auth?context=student&redirect=${encodeURIComponent(`/coach/${username}?membership_checkout=true`)}`;
      return;
    }

    setLoading(true);
    try {
      const result = await createMembershipCheckoutSession(coachId);
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (err) {
      console.error('Error starting membership checkout:', err);
      alert(err.message || 'Failed to start checkout');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (checkingStatus) return null;

  return (
    <div className="pricing-membership-featured">
      <div className={`cr-membership-card${isOwner ? ' cr-membership-owner' : ''}`}>
        {!isOwner && !isMember && (
          <div className="cr-membership-recommended-tag">
            <Crown size={12} /> Recommended
          </div>
        )}
        <div className="cr-membership-header">
          <div className="cr-membership-crown">
            <Crown size={20} />
          </div>
          <div>
            <h3>Membership</h3>
            <div className="cr-membership-price">
              <span className="cr-membership-currency">$</span>
              <span className="cr-membership-amount">{membershipConfig.priceUsd}</span>
              <span className="cr-membership-period">/month</span>
            </div>
          </div>
        </div>
        {membershipConfig.perks?.length > 0 && (
          <ul className="cr-membership-perks">
            {membershipConfig.perks.map((perk, i) => (
              <li key={i}>
                <Check size={14} />
                <span>{perk}</span>
              </li>
            ))}
          </ul>
        )}
        {isOwner ? (
          <div className="cr-membership-owner-badge">
            <ShieldCheck size={14} /> This is what your students see
          </div>
        ) : isMember ? (
          <div className="cr-membership-active-badge">
            <Crown size={14} /> Active Member
            {membershipCurrentPeriodEnd && (
              <span className="cr-membership-renew">Renews {formatDate(membershipCurrentPeriodEnd)}</span>
            )}
          </div>
        ) : (
          <button
            className="cr-membership-cta"
            onClick={handleSubscribe}
            disabled={loading}
          >
            {loading ? (
              <><Loader2 size={16} className="spin" /> Loading...</>
            ) : (
              <>Subscribe</>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default CoachingRenderer;
